// Global typography scale. Font-family agnostic — swap `fonts.display` to change brand font platform-wide.
// Rule: `displayXl` is RESERVED for hero moments only (max 3 uses across the platform: landing hero,
// careers hero, pricing hero). Using it elsewhere breaks the scale's meaning.

export const fonts = {
  display: '"Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  body:    '"Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  mono:    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace'
};

export const type = {
  // ─── Display scale ───
  displayXl: {                                         // Hero headline — landing/careers/pricing ONLY
    fontFamily: fonts.display,
    fontSize: "clamp(2.5rem, 7vw, 6.5rem)",            // 40px → 104px
    fontWeight: 700,
    lineHeight: 0.98,
    letterSpacing: "-0.02em",
    textWrap: "balance" as const
  },
  displayLg: {                                         // Major section H2 — "Why Bootpreneurs"
    fontFamily: fonts.display,
    fontSize: "clamp(2rem, 5vw, 3.5rem)",              // 32px → 56px
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: "-0.03em"
  },
  displayMd: {                                         // Detail page H1, sub-section H2
    fontFamily: fonts.display,
    fontSize: "clamp(1.75rem, 4vw, 3rem)",             // 28px → 48px
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: "-0.03em"
  },
  displaySm: {                                         // FAQ H2, "Why join" H2
    fontFamily: fonts.display,
    fontSize: "clamp(1.5rem, 3vw, 2.5rem)",            // 24px → 40px
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: "-0.02em"
  },

  // ─── Component-level titles ───
  cardTitle: {                                         // Role cards, product cards, panel heads
    fontFamily: fonts.display,
    fontSize: "clamp(1.25rem, 2vw, 1.875rem)",         // 20px → 30px
    fontWeight: 700,
    lineHeight: 1.08,
    letterSpacing: "-0.025em"
  },
  panelTitle: {                                        // Smaller panel/tile titles
    fontFamily: fonts.display,
    fontSize: "1.0625rem",                             // 17px fixed
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: "-0.01em"
  },
  articleH2: {                                         // Within long-form (blog, job body)
    fontFamily: fonts.display,
    fontSize: "1.75rem",                               // 28px fixed
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: "-0.02em"
  },

  // ─── Body ───
  bodyLg: {                                            // Hero sub-copy, lead paragraphs
    fontFamily: fonts.body,
    fontSize: "clamp(1rem, 1.3vw, 1.25rem)",           // 16px → 20px
    fontWeight: 400,
    lineHeight: 1.5
  },
  body: {                                              // Default body
    fontFamily: fonts.body,
    fontSize: "1rem",                                  // 16px
    fontWeight: 400,
    lineHeight: 1.65
  },
  bodySm: {                                            // Card body, metadata
    fontFamily: fonts.body,
    fontSize: "0.875rem",                              // 14px
    fontWeight: 400,
    lineHeight: 1.6
  },

  // ─── Accent text ───
  eyebrow: {                                           // Over-label above section titles
    fontFamily: fonts.mono,
    fontSize: "0.75rem",                               // 12px
    fontWeight: 600,
    letterSpacing: "0.18em",
    textTransform: "uppercase" as const
  },
  monoNumeric: {                                       // 01/02/03 numbers, large data figures
    fontFamily: fonts.mono,
    fontSize: "3.5rem",                                // 56px — scale up/down per context
    fontWeight: 200,
    letterSpacing: "-0.04em",
    lineHeight: 1
  },
  caption: {                                           // Micro-text, captions, helper labels
    fontFamily: fonts.body,
    fontSize: "0.6875rem",                             // 11px
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: "0.02em"
  }
};

export type TypeKey = keyof typeof type;
